// PompaBooking <-> NTAi postMessage protocol (contract RICK-NTA-02, NTAI-EMB-03).
//
//   Parent -> iframe   {type:"pb:handoff", token}           accepted only from an allowed
//                                                           parent origin, from window.parent
//   Iframe -> parent   {type:"ntai:ready"}                  once listening (to each allowed
//                                                           origin: none is verified yet)
//                      {type:"ntai:height", px}             ResizeObserver, throttled
//                      {type:"ntai:progress", pct}          the flow's own progress %
//                      {type:"ntai:done"}                   on reaching the done screen
//                      {type:"ntai:error", code}            exchange failures
//
// Everything after ready goes ONLY to the verified parent origin. No message ever
// carries answers or PII: the messenger builds each payload from numbers / a code.
import { apiBase } from '../config/runtime.js';

export const ERROR_CODES = ['handoff_invalid', 'unavailable', 'exchange_failed', 'network'];

export function isHandoff(event, { parents, parentWindow }) {
  if (!event || !parents.includes(event.origin)) return false;
  if (parentWindow && event.source !== parentWindow) return false;
  const d = event.data;
  return !!d && typeof d === 'object' && d.type === 'pb:handoff'
    && typeof d.token === 'string' && d.token.length > 0 && d.token.length <= 256;
}

export function createMessenger(parentWindow, parents) {
  let origin = null;
  const post = (msg, target) => {
    try { parentWindow.postMessage(msg, target); } catch { /* parent gone */ }
  };
  return {
    get origin() { return origin; },
    verify(o) { if (parents.includes(o)) origin = o; },
    // Before a handoff no origin is verified, so ready goes to each allowed parent;
    // postMessage drops it unless the parent really is that origin.
    ready() { for (const p of parents) post({ type: 'ntai:ready' }, p); },
    height(px) {
      if (!origin || !Number.isFinite(px)) return false;
      post({ type: 'ntai:height', px: Math.max(0, Math.ceil(px)) }, origin);
      return true;
    },
    progress(pct) {
      if (!origin || !Number.isFinite(pct)) return false;
      post({ type: 'ntai:progress', pct: Math.max(0, Math.min(100, Math.round(pct))) }, origin);
      return true;
    },
    done() {
      if (!origin) return false;
      post({ type: 'ntai:done' }, origin);
      return true;
    },
    error(code) {
      if (!origin) return false;
      post({ type: 'ntai:error', code: ERROR_CODES.includes(code) ? code : 'exchange_failed' }, origin);
      return true;
    },
  };
}

const fail = (code) => Object.assign(new Error(code), { code });

// POST /api/pb/exchange. Requires a JSON response: CloudFront answers an unknown API
// path with 200 text/html (the SPA's index.html), which must not read as success.
export async function exchangeHandoff(token, { fetchImpl = fetch, base = apiBase() } = {}) {
  let res;
  try {
    res = await fetchImpl(`${base}/pb/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handoff_token: token }),
      credentials: 'omit',
    });
  } catch {
    throw fail('network');
  }
  const type = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
  if (!/^application\/json\b/i.test(type.trim())) throw fail('unavailable');
  let data;
  try { data = await res.json(); } catch { throw fail('exchange_failed'); }
  if (res.status === 401) throw fail('handoff_invalid');
  if (res.status === 404) throw fail('unavailable');
  if (!res.ok || !data || typeof data.token !== 'string' || !data.token || typeof data.account !== 'object' || !data.account) {
    throw fail('exchange_failed');
  }
  return data;
}

// Listen for the handoff; exchange it once. Returns a stop() function.
export function listenForHandoff({ selfWindow, parentWindow, parents, messenger, exchange = exchangeHandoff, onSession }) {
  let state = 'waiting'; // waiting | exchanging | done
  const onMessage = (event) => {
    if (state !== 'waiting' || !isHandoff(event, { parents, parentWindow })) return;
    state = 'exchanging';
    messenger.verify(event.origin);
    Promise.resolve()
      .then(() => exchange(event.data.token))
      .then((data) => { state = 'done'; onSession(data); })
      .catch((e) => {
        state = 'waiting'; // the parent may retry with a fresh handoff
        messenger.error((e && e.code) || 'exchange_failed');
      });
  };
  selfWindow.addEventListener('message', onMessage);
  messenger.ready();
  return () => selfWindow.removeEventListener('message', onMessage);
}

// Throttled height reporter: at most one message per `interval` ms, always ending on the
// latest value, and never a repeat of the last height sent.
export function createHeightReporter(send, { interval = 120, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  let last = null;
  let pending = null;
  let timer = null;
  let lastAt = -Infinity;
  const now = () => Date.now();
  const flush = () => {
    timer = null;
    if (pending === null || pending === last) { pending = null; return; }
    if (send(pending)) last = pending;
    pending = null;
    lastAt = now();
  };
  return {
    report(px) {
      const h = Math.ceil(px);
      if (!Number.isFinite(h)) return;
      pending = h;
      if (timer) return;
      const wait = Math.max(0, interval - (now() - lastAt));
      if (wait === 0) flush();
      else timer = setTimer(flush, wait);
    },
    // Re-send the latest height (e.g. right after the origin is verified).
    reset() { last = null; },
    stop() { if (timer) clearTimer(timer); timer = null; },
  };
}
